/**
 * Port of {@class IamRuleExpressionSchema} —
 * backend/src/main/java/com/irian/iam/ruleengine/cel/IamRuleExpressionSchema.java
 *
 * Java production schema is a wrapper that materialises every registered
 * type as a cel-java StructType / MapType / ListType so cel-java's compiler
 * can reject unknown paths at compile time. @bufbuild/cel uses a different
 * type representation (CelObjectType / CelMapType / CelListType), so this
 * port keeps the SHAPE of the schema (root → fields → scope/collection
 * graph) and exposes lookups in our own TS terms. The suggestion engine
 * walks @bufbuild/cel ParsedExpr nodes using these data lookups instead of
 * cel-java StructType.findField.
 *
 * Platform substitution note: in Java the schema's typeProvider() feeds
 * cel-java's CelCompilerBuilder; in TS we do not type-check at parse time
 * (@bufbuild/cel parse() returns a ParsedExpr without type resolution),
 * so type validation falls to the IamCelValidator and the suggestion
 * engine's own path-walking logic.
 */

import {
    ExpressionFieldRegistry,
    type FieldDefinition,
    fieldDefinitionIsCollection,
    fieldDefinitionIsScope,
    fieldDefinitionIsDynamic,
    iamMultiRootSchemaBuilder,
} from '../api/ExpressionFieldRegistry'

const PARTY_SHAPED_CONTAINER_NAMES: ReadonlySet<string> = new Set(['party', 'parents', 'children'])

/**
 * One TS struct in the schema graph — mirrors the cel-java StructType the
 * Java implementation builds. fieldNames is the union of declared fields
 * plus dynamically-folded attributes plus relationship aliases. fieldDef
 * is null for folded attributes / aliases (their kind comes from the
 * registry's FieldNameProvider / aliasNames set).
 */
export type StructType = {
    readonly name: string
    readonly fieldNames: ReadonlySet<string>
    readonly fieldDefs: ReadonlyMap<string, FieldDefinition>
    readonly elementTypeOf?: string
    readonly scopeTypeOf?: string
}

export type ListTypeRef = {
    readonly kind: 'list'
    readonly elemType: TypeRef
}

export type MapTypeRef = {
    readonly kind: 'map'
}

export type ScalarTypeRef = {
    readonly kind: 'scalar'
    readonly fieldType: 'STRING' | 'ENUM' | 'NUMBER' | 'BOOLEAN' | 'DYN' | 'NULL' | 'DEFAULT'
}

export type StructTypeRef = {
    readonly kind: 'struct'
    readonly struct: StructType
}

export type TypeRef = ListTypeRef | MapTypeRef | ScalarTypeRef | StructTypeRef

export function isStruct(t: TypeRef | null): t is StructTypeRef {
    return t !== null && t.kind === 'struct'
}

export function isList(t: TypeRef | null): t is ListTypeRef {
    return t !== null && t.kind === 'list'
}

export function isMap(t: TypeRef | null): t is MapTypeRef {
    return t !== null && t.kind === 'map'
}

export function isScalar(t: TypeRef | null): t is ScalarTypeRef {
    return t !== null && t.kind === 'scalar'
}

type Snapshot = {
    readonly rootTypes: ReadonlyMap<string, StructType>
    readonly scopeTypes: ReadonlyMap<string, StructType>
    readonly collectionTypes: ReadonlyMap<string, ListTypeRef>
    readonly collectionElementTypes: ReadonlyMap<string, StructType>
    readonly elementTypeNameToCollection: ReadonlyMap<string, string>
    readonly attributesPaths: readonly string[]
}

export class IamRuleExpressionSchema {
    private readonly schemaRegistry: ExpressionFieldRegistry
    private snapshot: Snapshot
    private aliasNamesSet: ReadonlySet<string>

    constructor(registry: ExpressionFieldRegistry, aliasNames: ReadonlySet<string> = new Set()) {
        this.schemaRegistry = registry
        this.aliasNamesSet = new Set(aliasNames)
        this.snapshot = this.buildSnapshot()
    }

    static iamMultiRoot(): IamRuleExpressionSchema {
        return new IamRuleExpressionSchema(iamMultiRootSchemaBuilder().build())
    }

    rebuildAliases(newAliasNames: ReadonlySet<string>): void {
        this.aliasNamesSet = new Set(newAliasNames)
        this.snapshot = this.buildSnapshot()
    }

    isAlias(fieldName: string): boolean {
        return this.aliasNamesSet.has(fieldName)
    }

    rebuild(): void {
        this.snapshot = this.buildSnapshot()
    }

    registry(): ExpressionFieldRegistry {
        return this.schemaRegistry
    }

    rootNames(): ReadonlySet<string> {
        return new Set(this.snapshot.rootTypes.keys())
    }

    rootType(name: string): StructType | null {
        return this.snapshot.rootTypes.get(name) ?? null
    }

    structForPathRoot(firstSegment: string): StructType | null {
        return this.snapshot.rootTypes.get(firstSegment) ?? null
    }

    scopeStruct(scopeName: string): StructType | null {
        return this.snapshot.scopeTypes.get(scopeName) ?? null
    }

    collectionElementStruct(collectionName: string): StructType | null {
        return this.snapshot.collectionElementTypes.get(collectionName) ?? null
    }

    collectionForElementTypeName(elementTypeName: string): string | null {
        return this.snapshot.elementTypeNameToCollection.get(elementTypeName) ?? null
    }

    attributesPaths(): readonly string[] {
        return this.snapshot.attributesPaths
    }

    /**
     * Look up the FieldDefinition for a field of a registered struct type.
     */
    fieldDefinition(type: StructType, fieldName: string): FieldDefinition | null {
        return type.fieldDefs.get(fieldName) ?? null
    }

    /**
     * Return the FieldDefinition found by following a dotted path under one of
     * the registered roots. Delegates to the shared walker on
     * {@link ExpressionFieldRegistry} (issue 829).
     */
    resolveFieldDefinition(dottedPath: string | null): FieldDefinition | null {
        return this.schemaRegistry.resolveFieldDefinition(dottedPath)
    }

    /**
     * Resolve a dotted path against the schema and return the value type.
     */
    typeForPath(dottedPath: string): TypeRef | null {
        if (dottedPath.length === 0) return null
        const parts = dottedPath.split('.')
        const root = this.snapshot.rootTypes.get(parts[0])
        if (!root) return null
        let current: TypeRef = {kind: 'struct', struct: root}
        for (let i = 1; i < parts.length; i++) {
            current = this.stepInto(current, parts[i]) as TypeRef
            if (current === null) return null
        }
        return current
    }

    /**
     * Step into a type by a single field/key/element accessor. Mirrors
     * IamCelSuggestionEngine.stepInto in Java.
     */
    stepInto(type: TypeRef | null, field: string): TypeRef | null {
        if (type === null) return null
        if (type.kind === 'struct') {
            const def = type.struct.fieldDefs.get(field)
            if (def) return this.celTypeFor(def)
            if (type.struct.fieldNames.has(field)) {
                return {kind: 'scalar', fieldType: 'DYN'}
            }
            return null
        }
        if (type.kind === 'map') return {kind: 'scalar', fieldType: 'DYN'}
        if (type.kind === 'list') return type.elemType
        return null
    }

    private buildSnapshot(): Snapshot {
        const scopeTypes = new Map<string, StructType>()
        const collectionElementTypes = new Map<string, StructType>()
        const collectionTypes = new Map<string, ListTypeRef>()
        const elementTypeNameToCollection = new Map<string, string>()

        const scopeNames = this.collectScopeNames()
        const collectionNames = this.collectCollectionNames(scopeNames)
        for (const collectionName of collectionNames) {
            const typeName = collectionElementTypeName(collectionName)
            const filterFields = this.schemaRegistry.collectionFilterFields(collectionName)
            const element = this.structFromFields(typeName, filterFields, collectionName)
            collectionElementTypes.set(collectionName, element)
            collectionTypes.set(collectionName, {kind: 'list', elemType: {kind: 'struct', struct: element}})
            elementTypeNameToCollection.set(typeName, collectionName)
        }
        // NOTE: a scope can declare a SCOPE field referencing another scope
        // (the certification `access` schema nests profile → resource → system
        // and identity → party). Build in dependency order so a parent scope
        // is built only after every scope it references exists. Mirrors the
        // Java IamRuleExpressionSchema dependency-ordered scope build.
        const pending = new Set<string>(scopeNames)
        while (pending.size > 0) {
            const ready: string[] = []
            for (const scopeName of pending) {
                if (nestedScopesBuilt(this.schemaRegistry.scopeFields(scopeName), scopeTypes)) {
                    ready.push(scopeName)
                }
            }
            if (ready.length === 0) ready.push(...pending)
            for (const scopeName of ready) {
                const typeName = scopeTypeName(scopeName)
                const fields = this.schemaRegistry.scopeFields(scopeName)
                scopeTypes.set(scopeName, this.structFromFields(typeName, fields, scopeName))
                pending.delete(scopeName)
            }
        }
        const rootTypes = new Map<string, StructType>()
        for (const root of this.schemaRegistry.rootNames()) {
            const typeName = rootTypeName(root)
            const fields = this.schemaRegistry.rootFields(root)
            rootTypes.set(root, this.structFromFields(typeName, fields, root))
        }
        const attributesPaths = this.computeAttributesPaths()
        return {
            rootTypes,
            scopeTypes,
            collectionTypes,
            collectionElementTypes,
            elementTypeNameToCollection,
            attributesPaths,
        }
    }

    private collectScopeNames(): ReadonlySet<string> {
        const names = new Set<string>()
        for (const root of this.schemaRegistry.rootNames()) {
            this.collectScopeNamesFrom(this.schemaRegistry.rootFields(root), names)
        }
        return names
    }

    private collectScopeNamesFrom(fields: ReadonlyMap<string, FieldDefinition>, names: Set<string>): void {
        for (const def of fields.values()) {
            if (fieldDefinitionIsScope(def) && !names.has(def.name)) {
                names.add(def.name)
                this.collectScopeNamesFrom(this.schemaRegistry.scopeFields(def.name), names)
            }
        }
    }

    private collectCollectionNames(scopeNames: ReadonlySet<string>): ReadonlySet<string> {
        const names = new Set<string>()
        for (const root of this.schemaRegistry.rootNames()) {
            this.collectCollectionNamesFrom(this.schemaRegistry.rootFields(root), names)
        }
        for (const scopeName of scopeNames) {
            this.collectCollectionNamesFrom(this.schemaRegistry.scopeFields(scopeName), names)
        }
        return names
    }

    private collectCollectionNamesFrom(fields: ReadonlyMap<string, FieldDefinition>, sink: Set<string>): void {
        for (const def of fields.values()) {
            if (fieldDefinitionIsCollection(def)) sink.add(def.name)
        }
    }

    private structFromFields(typeName: string, fields: ReadonlyMap<string, FieldDefinition>, containerName: string): StructType {
        const fieldNames = new Set<string>()
        const fieldDefs = new Map<string, FieldDefinition>()
        for (const def of fields.values()) {
            fieldNames.add(def.name)
            fieldDefs.set(def.name, def)
        }
        // NOTE: Fold registered attribute names into the struct's field set when this
        // container declares a DYNAMIC field.
        let hasDynamic = false
        for (const def of fields.values()) {
            if (fieldDefinitionIsDynamic(def)) {
                hasDynamic = true
                break
            }
        }
        if (hasDynamic) {
            const provider = this.schemaRegistry.fieldNameProvider(`${containerName}.attributes`)
            if (provider !== null) {
                for (const s of provider.getFieldNames('')) {
                    if (!fieldNames.has(s.name)) fieldNames.add(s.name)
                }
            }
        }
        if (PARTY_SHAPED_CONTAINER_NAMES.has(containerName)) {
            for (const alias of this.aliasNamesSet) fieldNames.add(alias)
        }
        return {name: typeName, fieldNames, fieldDefs}
    }

    private celTypeFor(def: FieldDefinition): TypeRef {
        switch (def.valueType.name) {
            case 'STRING':
            case 'ENUM':
                return {kind: 'scalar', fieldType: 'STRING'}
            case 'BOOLEAN':
                return {kind: 'scalar', fieldType: 'BOOLEAN'}
            case 'NUMBER':
                return {kind: 'scalar', fieldType: 'NUMBER'}
            case 'DYNAMIC':
                return {kind: 'map'}
            case 'SCOPE': {
                const s = this.snapshot?.scopeTypes.get(def.name)
                return s ? {kind: 'struct', struct: s} : {kind: 'map'}
            }
            case 'COLLECTION': {
                const c = this.snapshot?.collectionTypes.get(def.name)
                return c ?? {kind: 'list', elemType: {kind: 'scalar', fieldType: 'DYN'}}
            }
            default:
                return {kind: 'scalar', fieldType: 'DYN'}
        }
    }

    private computeAttributesPaths(): readonly string[] {
        const out: string[] = []
        for (const root of this.schemaRegistry.rootNames()) {
            this.collectAttributesUnder(root, this.schemaRegistry.rootFields(root), out)
        }
        return out
    }

    private collectAttributesUnder(pathSoFar: string, fields: ReadonlyMap<string, FieldDefinition>, out: string[]): void {
        for (const def of fields.values()) {
            const childPath = `${pathSoFar}.${def.name}`
            if (fieldDefinitionIsDynamic(def)) {
                out.push(childPath)
            } else if (fieldDefinitionIsScope(def)) {
                this.collectAttributesUnder(childPath, this.schemaRegistry.scopeFields(def.name), out)
            }
        }
    }
}

function nestedScopesBuilt(fields: ReadonlyMap<string, FieldDefinition>, scopeTypes: ReadonlyMap<string, StructType>): boolean {
    for (const def of fields.values()) {
        if (fieldDefinitionIsScope(def) && !scopeTypes.has(def.name)) return false
    }
    return true
}

function rootTypeName(rootName: string): string {
    return capitalize(rootName)
}

function scopeTypeName(scopeName: string): string {
    return `${capitalize(scopeName)}Scope`
}

function collectionElementTypeName(collectionName: string): string {
    return `${capitalize(collectionName)}Element`
}

function capitalize(s: string): string {
    if (s.length === 0) return s
    return s.charAt(0).toUpperCase() + s.substring(1)
}
